import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import PlatformIcon from "@/components/PlatformIcon";
import { VerifiedBadge } from "@/components/Navbar";

export function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 bg-[#22c55e]/10 text-[#4ade80] border border-[#22c55e]/30 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-widest uppercase"
          data-testid="live-badge">
      <span className="live-dot w-1.5 h-1.5 rounded-full bg-[#22c55e]" aria-hidden="true" />
      Live
    </span>
  );
}

export default function CreatorCard({ creator, compact = false }) {
  const live = creator.live || {};
  const stats = creator.stats || {};
  const livePlatform = (creator.platforms || []).find((p) => p.platform === live.platform) || (creator.platforms || [])[0] || {};
  return (
    <div className="card-dt overflow-hidden group" data-testid={`creator-card-${creator.slug}`}>
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            <img src={creator.avatar || "/assets/logo.png"} alt={`${creator.creator_name} profile photo`}
                 className="w-14 h-14 rounded-full object-cover ring-2 ring-dt-border" loading="lazy" />
            {live.live && (
              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#22c55e] border-2 border-dt-card live-dot" aria-label="Live now" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-heading font-semibold text-dt-text truncate">{creator.creator_name}</span>
              {creator.verified && <VerifiedBadge />}
              {live.live && <LiveBadge />}
            </div>
            <div className="flex items-center gap-2 mt-1 text-dt-muted text-xs">
              <PlatformIcon platform={live.live ? live.platform : (creator.platforms || [])[0]?.platform} />
              <span>{creator.category}</span>
              {stats.followers ? <span>· {stats.followers.toLocaleString()} followers</span> : null}
            </div>
          </div>
        </div>

        {live.live ? (
          <p className="mt-3 text-sm text-dt-accent2 truncate" data-testid="stream-title">{live.title}</p>
        ) : (
          <p className="mt-3 text-sm text-dt-muted line-clamp-2">{creator.bio || "Currently offline"}</p>
        )}

        {!compact && (
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="bg-dt-bg2 rounded-lg py-2 px-1">
              <p className="text-xs text-dt-muted">Top tip (mo)</p>
              <p className="text-sm font-semibold text-dt-silver font-mono2">₹{(stats.top_tip_month || 0).toLocaleString()}</p>
            </div>
            <div className="bg-dt-bg2 rounded-lg py-2 px-1">
              <p className="text-xs text-dt-muted">All-time tips</p>
              <p className="text-sm font-semibold text-dt-silver font-mono2">₹{(stats.total_tips || 0).toLocaleString()}</p>
            </div>
            <div className="bg-dt-bg2 rounded-lg py-2 px-1">
              <p className="text-xs text-dt-muted">Viewers</p>
              <p className="text-sm font-semibold text-dt-silver font-mono2">{live.live ? (live.viewers || 0).toLocaleString() : "—"}</p>
            </div>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          {live.live ? (
            <a href={livePlatform.url || "#"} target="_blank" rel="noopener noreferrer"
               className="flex-1 text-center bg-[#22c55e]/15 text-[#4ade80] border border-[#22c55e]/30 rounded-full px-4 py-2 text-sm font-semibold hover:bg-[#22c55e]/25 transition-colors"
               data-testid="watch-live-btn">
              Watch Live
            </a>
          ) : (
            <span className="flex-1 text-center bg-dt-bg2 text-dt-muted border border-dt-border rounded-full px-4 py-2 text-sm">Offline</span>
          )}
          <Link to={`/creators/${creator.slug}`}
                className="inline-flex items-center gap-1 bg-dt-card border border-dt-border rounded-full px-4 py-2 text-sm text-dt-text hover:border-dt-accent transition-colors"
                data-testid="view-profile-btn">
            Profile <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
